import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, NavLink } from 'react-router'
import { useSelector } from 'react-redux'
import classnames from 'classnames'

import store from '../../store'

import { replaceRouteParams, checkIfParamsRelatedLink, getParameterFromPath } from '../../helpers/url'

import { setSelectedPlatform } from '../../modules/actions/app'
import { selectedPlatformSelector } from '../../modules/selectors/app'
import { selectedAdAccountsSelector } from '../../modules/selectors/adAccounts'
import { selectedPartnershipIdSelector } from '../../modules/selectors/partnerships'

import { ROUTE_PARAMS } from '../../constants/routes'

function InternalLink({ to, navLink, children, className, onClick, ...props }) {
  // There are a lot of pages related on different parameters like selected platform,AdAccount and etc
  // this component dynamically replace links through app on parameters change
  const [linkTouched, setLinkTouched] = useState(false)

  const selectedPlatform = useSelector(selectedPlatformSelector)
  const selectedPartnershipId = useSelector(selectedPartnershipIdSelector)
  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)

  const { id: selectedAdAccountId } = selectedAdAccount || {}

  const isParamsRelatedLink = useMemo(() => checkIfParamsRelatedLink(to), [to])
  const populatedParams = {
    [ROUTE_PARAMS.partnership]: selectedPartnershipId,
    [ROUTE_PARAMS.platform]: selectedPlatform,
    [ROUTE_PARAMS.adAccount]: selectedAdAccountId
  }

  let newLinkTo

  if (isParamsRelatedLink) {
    newLinkTo = replaceRouteParams(to, populatedParams)
  } else {
    newLinkTo = to
  }

  const touchStartEvent = () => {
    setLinkTouched(true)
  }

  const handleRouteChange = useCallback(() => {
    // check and handle platform link change during Route switch
    const platformFromLink = getParameterFromPath(ROUTE_PARAMS.platform, to)

    if (platformFromLink && platformFromLink !== selectedPlatform) {
      // set new platform to reducer if platforms changes during route change
      store.dispatch(setSelectedPlatform(platformFromLink))
    }

    onClick && onClick()
  }, [selectedPlatform, to, onClick])

  useEffect(() => {
    // we need this to simulate changing css on click events for mobile devices (tap in our case)
    let timeOut

    if (linkTouched) {
      timeOut = setTimeout(() => {
        setLinkTouched(false)
      }, 100)
    }
    return () => {
      clearTimeout(timeOut)
    }
  }, [linkTouched])

  return navLink ? (
    <NavLink
      {...props}
      className={className}
      to={newLinkTo}
      onTouchStart={touchStartEvent}
      onClick={handleRouteChange}
      end // "end" prop ensures that the active class is only applied when there's an exact match:
    >
      {children}
    </NavLink>
  ) : (
    <Link
      onTouchStart={touchStartEvent}
      className={classnames(className, { linkTouched: linkTouched })}
      onClick={handleRouteChange}
      {...props}
      to={newLinkTo}
    >
      {children}
    </Link>
  )
}

export default InternalLink
